// src/pages/accountability/AccountabilityPage.tsx
import React, { useState, useEffect } from 'react';
import { Shield, Map, BarChart3, History, Radio, Calendar } from 'lucide-react';
import { AccountabilityDashboard, CanvasserLocationMap } from '../../components/accountability';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { locationService, AccountabilityRecord } from '../../services/location.service';

type ViewMode = 'dashboard' | 'map';
type MapMode = 'live' | 'rewind';

const COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

const AccountabilityPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { selectedProjectId } = useProject();
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [mapMode, setMapMode] = useState<MapMode>('live');
  const [rewindDate, setRewindDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [rewindPaths, setRewindPaths] = useState<Map<string, { userName: string; records: AccountabilityRecord[] }>>(new Map());
  const [loadingRewind, setLoadingRewind] = useState(false);

  // Load historical data when in rewind mode
  useEffect(() => {
    if (mapMode !== 'rewind' || !selectedProjectId || !rewindDate) return;

    const loadHistorical = async () => {
      setLoadingRewind(true);
      try {
        const paths = await locationService.getHistoricalCanvasserPaths(
          selectedProjectId,
          new Date(rewindDate + 'T12:00:00')
        );
        setRewindPaths(paths);
      } catch (error) {
        console.error('Error loading historical paths:', error);
      } finally {
        setLoadingRewind(false);
      }
    };

    loadHistorical();
  }, [mapMode, selectedProjectId, rewindDate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Accountability Center</h1>
                <p className="text-sm text-gray-600">
                  Monitor field staff activity and location verification
                </p>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-white rounded-lg shadow-sm border p-1">
              <button
                onClick={() => setViewMode('dashboard')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'dashboard'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'map'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Map className="w-4 h-4" />
                Map
              </button>
            </div>
          </div>
        </div>

        {/* Map Mode Controls */}
        {viewMode === 'map' && (
          <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Live / Rewind Toggle */}
            <div className="flex items-center bg-white rounded-lg shadow-sm border p-1">
              <button
                onClick={() => setMapMode('live')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  mapMode === 'live'
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Radio className="w-4 h-4" />
                Live
              </button>
              <button
                onClick={() => setMapMode('rewind')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  mapMode === 'rewind'
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <History className="w-4 h-4" />
                Rewind
              </button>
            </div>

            {/* Date Picker for Rewind */}
            {mapMode === 'rewind' && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={rewindDate}
                  onChange={(e) => setRewindDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
                {loadingRewind && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                )}
                <span className="text-sm text-gray-500">
                  {rewindPaths.size} canvasser{rewindPaths.size !== 1 ? 's' : ''} found
                </span>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {viewMode === 'dashboard' ? (
          <AccountabilityDashboard projectId={userProfile?.projectId} />
        ) : mapMode === 'live' ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <CanvasserLocationMap
              projectId={userProfile?.projectId}
              height="calc(100vh - 260px)"
              showLegend
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Rewind Map with Historical Paths */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <CanvasserLocationMap
                projectId={userProfile?.projectId}
                height="calc(100vh - 360px)"
                showLegend
                rewindMode
                rewindDate={new Date(rewindDate + 'T12:00:00')}
              />
            </div>

            {/* Rewind Path Legend */}
            {rewindPaths.size > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Canvasser Activity on {new Date(rewindDate + 'T12:00:00').toLocaleDateString()}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {Array.from(rewindPaths.entries()).map(([userId, data], index) => (
                    <div key={userId} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{data.userName}</p>
                        <p className="text-xs text-gray-500">{data.records.length} actions</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountabilityPage;
